import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Container,
    Typography,
    AppBar,
    Toolbar,
    Button,
    Card,
    CardContent,
    Chip,
    Alert,
    Paper,
    Tabs,
    Tab,
    CircularProgress,
    LinearProgress,
} from '@mui/material';
import {
    Security as SecurityIcon,
    Logout as LogoutIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Info as InfoIcon,
    Refresh as RefreshIcon,
    Download as DownloadIcon,
} from '@mui/icons-material';
import SecurityMonitor from '../components/SecurityMonitor';
import SimpleIPBlockingManager from '../components/SimpleIPBlockingManager';
import AdminSettings from '../components/AdminSettings';
import AttackMonitor from '../components/AttackMonitor';
import BotMonitoringPanel from '../components/admin/BotMonitoringPanel';
// import { useSystemStatusOptimized } from '../hooks/useSystemStatusOptimized';
// import { useAdminSettingsOptimized } from '../hooks/useAdminSettingsOptimized';
import { SecurityAudit } from '../utils/security';

interface AdminDashboardProps {
    sessionId: string;
    onLogout: () => void;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`admin-tabpanel-${index}`}
            aria-labelledby={`admin-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({ sessionId, onLogout }) => {
    const [sessionInfo, setSessionInfo] = useState<any>(null);
    const [activeTab, setActiveTab] = useState(0);
    const [securityEvents, setSecurityEvents] = useState<any[]>([]);

    // Simple system status state
    const [systemMetrics, setSystemMetrics] = useState<any>(null);
    const [systemHealth, setSystemHealth] = useState<any>(null);
    const [isSystemLoading] = useState(false);
    const [isSystemRefreshing, setIsSystemRefreshing] = useState(false);
    const [systemError, setSystemError] = useState<string | null>(null);

    const refreshSystem = useCallback(async () => {
        setIsSystemRefreshing(true);
        try {
            // Gather real browser-based metrics
            const startTime = performance.now();

            // Memory metrics (if available)
            let memoryUsage = 0;
            let memoryTotal = 0;
            if ('memory' in performance && (performance as any).memory) {
                const memory = (performance as any).memory;
                memoryUsage = Math.round((memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100);
                memoryTotal = Math.round(memory.jsHeapSizeLimit / (1024 * 1024)); // MB
            }

            // Connection info (if available)
            let connectionType = 'Unknown';
            let effectiveType = 'Unknown';
            if ('connection' in navigator && (navigator as any).connection) {
                const conn = (navigator as any).connection;
                connectionType = conn.type || 'Unknown';
                effectiveType = conn.effectiveType || 'Unknown';
            }

            // Calculate response time
            const responseTime = Math.round(performance.now() - startTime);

            // Get navigation timing
            const navTiming = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
            const pageLoadTime = navTiming ? Math.round(navTiming.loadEventEnd - navTiming.fetchStart) : 0;

            // Calculate uptime (time since page load)
            const uptime = Math.floor(performance.now() / 1000);

            // Determine health status
            const issues: string[] = [];
            const recommendations: string[] = [];
            let healthScore = 100;

            if (memoryUsage > 80) {
                issues.push('High memory usage detected');
                recommendations.push('Consider refreshing the page to free up memory');
                healthScore -= 20;
            }

            if (responseTime > 100) {
                issues.push('Slow response time detected');
                recommendations.push('Check network connection');
                healthScore -= 10;
            }

            const overall = healthScore >= 80 ? 'healthy' : healthScore >= 60 ? 'warning' : 'critical';

            setSystemHealth({
                overall,
                status: overall,
                score: healthScore,
                uptime,
                responseTime,
                pageLoadTime,
                lastChecked: new Date().toISOString(),
                issues,
                recommendations: recommendations.length > 0 ? recommendations : ['System running smoothly', 'Regular monitoring recommended'],
                connectionType,
                effectiveType
            });

            setSystemMetrics({
                memory: {
                    usage: memoryUsage,
                    total: memoryTotal,
                    value: memoryUsage,
                    unit: '%',
                    label: 'Memory Usage'
                },
                performance: {
                    value: Math.min(100, Math.round((responseTime / 100) * 100)),
                    unit: '%',
                    responseTime,
                    label: 'Performance Score'
                },
                pageLoad: {
                    value: Math.min(100, Math.round((pageLoadTime / 3000) * 100)),
                    unit: '%',
                    time: pageLoadTime,
                    label: 'Page Load Time'
                },
                connection: {
                    type: connectionType,
                    effectiveType,
                    label: 'Connection'
                }
            });

            setSystemError(null);
        } catch (err) {
            console.error('Failed to refresh system status:', err);
            setSystemError('Failed to refresh system status');
        } finally {
            setIsSystemRefreshing(false);
        }
    }, []);

    const exportMetrics = useCallback(() => {
        return JSON.stringify({
            metrics: systemMetrics,
            health: systemHealth,
            timestamp: new Date().toISOString()
        }, null, 2);
    }, [systemMetrics, systemHealth]);

    const isSystemHealthy = useCallback(() => {
        return systemHealth?.status === 'healthy';
    }, [systemHealth]);

    const getSystemUptime = useCallback(() => {
        if (!systemHealth?.uptime) return 'Unknown';
        const seconds = systemHealth.uptime;
        const days = Math.floor(seconds / 86400);
        const hours = Math.floor((seconds % 86400) / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        return `${days}d ${hours}h ${minutes}m`;
    }, [systemHealth]);

    const getLastRefreshTime = useCallback(() => {
        if (!systemHealth?.lastChecked) return 'Never';
        try {
            const date = new Date(systemHealth.lastChecked);
            return date.toLocaleTimeString();
        } catch {
            return 'Never';
        }
    }, [systemHealth]);

    // Admin settings are now handled by the AdminSettings component

    useEffect(() => {
        // Session validation handled by Firebase
        setSessionInfo({ username: 'Admin', loginTime: Date.now(), lastActivity: Date.now() });

        const loadSecurityEvents = async () => {
            try {
                const events = await SecurityAudit.getAuditLog();
                setSecurityEvents(events);
            } catch (error) {
                console.error('Failed to load security events:', error);
            }
        };

        // System status is now handled by the useSystemStatus hook

        // Admin settings are now handled by the useAdminSettings hook

        loadSecurityEvents();

        // Load initial system metrics
        refreshSystem();

        // Auto-refresh system metrics every 30 seconds
        const systemInterval = setInterval(refreshSystem, 30000);

        // Firebase handles session management automatically

        return () => {
            clearInterval(systemInterval);
        };
    }, [sessionId, onLogout, refreshSystem]);

    const handleLogout = () => {
        // Firebase handles logout automatically
        onLogout();
    };

    const handleRefresh = async () => {
        try {
            // Refresh all data
            const events = await SecurityAudit.getAuditLog();
            setSecurityEvents(events);

            // Refresh system status
            await refreshSystem();
        } catch (error) {
            console.error('Failed to refresh data:', error);
        }
    };

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    const criticalEvents = securityEvents.filter((event: any) => event.severity === 'critical');
    const highEvents = securityEvents.filter((event: any) => event.severity === 'high');
    const mediumEvents = securityEvents.filter((event: any) => event.severity === 'medium');

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            {/* App Bar */}
            <AppBar position="static">
                <Toolbar>
                    <SecurityIcon sx={{ mr: 2 }} />
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                        Admin Dashboard
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Chip
                            label={`Session: ${sessionInfo?.username || 'Admin'}`}
                            color="secondary"
                            size="small"
                        />
                        <Button
                            color="inherit"
                            onClick={handleLogout}
                            startIcon={<LogoutIcon />}
                        >
                            Logout
                        </Button>
                    </Box>
                </Toolbar>
            </AppBar>

            {/* Main Content */}
            <Box sx={{ flexGrow: 1, p: 3 }}>
                <Container maxWidth="xl">
                    {/* Quick Stats */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                        <Card sx={{ minWidth: 200, flex: 1 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box>
                                        <Typography color="textSecondary" gutterBottom>
                                            Critical Events
                                        </Typography>
                                        <Typography variant="h4" color="error">
                                            {criticalEvents.length}
                                        </Typography>
                                    </Box>
                                    <ErrorIcon color="error" sx={{ fontSize: 40 }} />
                                </Box>
                            </CardContent>
                        </Card>

                        <Card sx={{ minWidth: 200, flex: 1 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box>
                                        <Typography color="textSecondary" gutterBottom>
                                            High Events
                                        </Typography>
                                        <Typography variant="h4" color="warning.main">
                                            {highEvents.length}
                                        </Typography>
                                    </Box>
                                    <WarningIcon color="warning" sx={{ fontSize: 40 }} />
                                </Box>
                            </CardContent>
                        </Card>

                        <Card sx={{ minWidth: 200, flex: 1 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box>
                                        <Typography color="textSecondary" gutterBottom>
                                            Medium Events
                                        </Typography>
                                        <Typography variant="h4" color="info.main">
                                            {mediumEvents.length}
                                        </Typography>
                                    </Box>
                                    <InfoIcon color="info" sx={{ fontSize: 40 }} />
                                </Box>
                            </CardContent>
                        </Card>

                        <Card sx={{ minWidth: 200, flex: 1 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box>
                                        <Typography color="textSecondary" gutterBottom>
                                            Active Sessions
                                        </Typography>
                                        <Typography variant="h4" color="success.main">
                                            1
                                        </Typography>
                                    </Box>
                                    <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>

                    {/* Session Info Alert */}
                    {sessionInfo && (
                        <Alert severity="info" sx={{ mb: 3 }}>
                            <Typography variant="body2">
                                <strong>Logged in as:</strong> {sessionInfo.username} |
                                <strong> Login time:</strong> {new Date(sessionInfo.loginTime).toLocaleString()} |
                                <strong> Last activity:</strong> {new Date(sessionInfo.lastActivity).toLocaleString()}
                            </Typography>
                        </Alert>
                    )}

                    {/* Tabs */}
                    <Paper sx={{ width: '100%' }}>
                        <Tabs value={activeTab} onChange={handleTabChange} aria-label="admin tabs">
                            <Tab label="Attack Monitor" />
                            <Tab label="Security Monitor" />
                            <Tab label="Bot Detection" />
                            <Tab label="System Status" />
                            <Tab label="IP Blocking" />
                            <Tab label="Settings" />
                        </Tabs>
                    </Paper>

                    {/* Tab Panels */}
                    <TabPanel value={activeTab} index={0}>
                        <AttackMonitor />
                    </TabPanel>

                    <TabPanel value={activeTab} index={1}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h5">Security Monitor</Typography>
                            <Button
                                startIcon={<RefreshIcon />}
                                onClick={handleRefresh}
                                variant="outlined"
                            >
                                Refresh
                            </Button>
                        </Box>
                        <SecurityMonitor />
                    </TabPanel>

                    <TabPanel value={activeTab} index={2}>
                        <BotMonitoringPanel />
                    </TabPanel>

                    <TabPanel value={activeTab} index={3}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h5">System Status</Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    startIcon={<RefreshIcon />}
                                    onClick={handleRefresh}
                                    variant="outlined"
                                    disabled={isSystemRefreshing}
                                >
                                    {isSystemRefreshing ? <CircularProgress size={20} /> : 'Refresh'}
                                </Button>
                                <Button
                                    startIcon={<DownloadIcon />}
                                    onClick={() => {
                                        const data = exportMetrics();
                                        const blob = new Blob([data], { type: 'application/json' });
                                        const url = URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = 'system-metrics.json';
                                        document.body.appendChild(a);
                                        a.click();
                                        document.body.removeChild(a);
                                        URL.revokeObjectURL(url);
                                    }}
                                    variant="outlined"
                                >
                                    Export
                                </Button>
                            </Box>
                        </Box>

                        {/* System Health Alert */}
                        {systemHealth && systemHealth.overall && systemHealth.overall !== 'healthy' && (
                            <Alert
                                severity={systemHealth.overall === 'critical' ? 'error' : 'warning'}
                                sx={{ mb: 3 }}
                            >
                                <Typography variant="subtitle2">
                                    System Health: {systemHealth.overall.toUpperCase()} (Score: {systemHealth.score || 0}/100)
                                </Typography>
                                {systemHealth.issues && systemHealth.issues.length > 0 && (
                                    <ul>
                                        {systemHealth.issues.map((issue: string, index: number) => (
                                            <li key={index}>{issue}</li>
                                        ))}
                                    </ul>
                                )}
                            </Alert>
                        )}

                        {/* System Error */}
                        {systemError && (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                {systemError}
                            </Alert>
                        )}

                        {/* Loading State */}
                        {isSystemLoading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress />
                            </Box>
                        )}

                        {/* System Status Content */}
                        {systemHealth && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                {/* System Overview */}
                                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                                    <Box sx={{ flex: 1, minWidth: 300 }}>
                                        <Card>
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom>
                                                    System Overview
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Typography variant="body2">Overall Status</Typography>
                                                        <Chip
                                                            label={systemHealth.overall}
                                                            color={systemHealth.overall === 'healthy' ? 'success' : systemHealth.overall === 'warning' ? 'warning' : 'error'}
                                                            size="small"
                                                        />
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Typography variant="body2">Health Score</Typography>
                                                        <Typography variant="body2" color="primary">
                                                            {systemHealth.score}/100
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Typography variant="body2">Uptime</Typography>
                                                        <Typography variant="body2" color="primary">
                                                            {getSystemUptime()}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Typography variant="body2">Response Time</Typography>
                                                        <Typography variant="body2">{systemHealth.responseTime}ms</Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Typography variant="body2">System Healthy</Typography>
                                                        <Chip
                                                            label={isSystemHealthy() ? 'Yes' : 'No'}
                                                            color={isSystemHealthy() ? 'success' : 'error'}
                                                            size="small"
                                                        />
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Box>

                                    {/* Performance Metrics */}
                                    <Box sx={{ flex: 1, minWidth: 300 }}>
                                        <Card>
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom>
                                                    Performance Metrics
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                    {systemMetrics && Object.keys(systemMetrics).length > 0 ? (
                                                        Object.entries(systemMetrics).map(([key, metric]: [string, any]) => (
                                                            <Box key={key}>
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                                                        {metric.label || key}
                                                                    </Typography>
                                                                    <Typography variant="body2">
                                                                        {metric.value !== undefined ? `${metric.value}${metric.unit}` :
                                                                            metric.type || metric.effectiveType || 'N/A'}
                                                                    </Typography>
                                                                </Box>
                                                                {metric.value !== undefined && (
                                                                    <LinearProgress
                                                                        variant="determinate"
                                                                        value={Math.min(metric.value, 100)}
                                                                        color={metric.value > 80 ? 'error' : metric.value > 50 ? 'warning' : 'success'}
                                                                    />
                                                                )}
                                                                {metric.responseTime && (
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        Response: {metric.responseTime}ms
                                                                    </Typography>
                                                                )}
                                                                {metric.time && (
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        Load Time: {metric.time}ms
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        ))
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            No metrics available
                                                        </Typography>
                                                    )}
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2">Response Time</Typography>
                                                        <Typography variant="body2">{systemHealth?.responseTime || 0}ms</Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2">Last Refresh</Typography>
                                                        <Typography variant="body2">
                                                            {getLastRefreshTime()}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2">System Status</Typography>
                                                        <Typography variant="body2" color={isSystemHealthy() ? 'success.main' : 'error.main'}>
                                                            {isSystemHealthy() ? 'Healthy' : 'Issues Detected'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {/* Fallback for old system status */}
                        {!systemMetrics && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Loading system status...
                                </Typography>
                            </Box>
                        )}
                    </TabPanel>

                    <TabPanel value={activeTab} index={4}>
                        <SimpleIPBlockingManager />
                    </TabPanel>

                    <TabPanel value={activeTab} index={5}>
                        <AdminSettings onSave={(settings) => console.log('Settings saved:', settings)} />
                    </TabPanel>
                </Container>
            </Box>
        </Box>
    );
};

export default AdminDashboard; 